import constants from '../../../../constants';

const API_BASE = constants.baseApiHost;
const API_KEY = constants.apikey;

export interface AccountPasskey {
  aaguid: string;
  createdAt: string;
  id: number;
  /** The address it was added from; null on rows from before it was recorded. */
  ipAddress: string | null;
  isBackedUp: boolean;
  isBackupEligible: boolean;
  lastUsedAt: string | null;
  name: string;
  /** Set once removed. Only listed when asked for. */
  removedAt: string | null;
  /** The browser it was added from. */
  userAgent: string | null;
}

export interface PasskeyOptions {
  challengeId: string;
  options: string;
}

/** Proves it's the account holder: a code on an account with MFA, the password on one without. */
export interface PasskeyConfirmation {
  otp?: string;
  password?: string;
}

/** An API refusal, carrying the first error key (e.g. "model.invalidCredentials"). */
export class PasskeyApiError extends Error {
  readonly key: string;

  constructor(key: string) {
    super(key);
    this.key = key;
  }
}

const getSession = () => {
  const raw = localStorage['session'];
  return raw && raw !== 'undefined' ? JSON.parse(raw) : null;
};

const getHeaders = (): Record<string, string> => {
  const session = getSession();
  return {
    ApiKey: API_KEY || '',
    'Content-Type': 'application/json',
    Authorization: session?.key ? `bearer ${session.key}` : ''
  };
};

const request = async <T>(path: string, init: RequestInit): Promise<T> => {
  const response = await fetch(`${API_BASE}/api/v3/account/${path}`, {
    ...init,
    headers: getHeaders()
  });

  // A successful DELETE answers 204 with no body.
  if (response.status === 204) {
    return null as T;
  }

  let json: any = null;
  try {
    json = await response.json();
  } catch {
    json = null;
  }

  if (!response.ok || json?.success === false || json === null) {
    throw new PasskeyApiError(
      json?.error?.[0]?.error ?? `http.${response.status}`
    );
  }

  return json.data as T;
};

/** Live passkeys, plus the removed ones (with removedAt set) when asked for. */
export const fetchPasskeys = (
  accountId: number | string,
  includeRemoved = false
) =>
  request<AccountPasskey[]>(
    `${accountId}/passkey${includeRemoved ? '?includeRemoved=true' : ''}`,
    { method: 'GET' }
  );

export const beginPasskeyRegistration = (
  accountId: number | string,
  confirmation: PasskeyConfirmation
) =>
  request<PasskeyOptions>(`${accountId}/passkey/options`, {
    method: 'POST',
    body: JSON.stringify(confirmation)
  });

export const completePasskeyRegistration = (
  accountId: number | string,
  body: { challengeId: string; credential: string; name: string }
) =>
  request<AccountPasskey>(`${accountId}/passkey`, {
    method: 'POST',
    body: JSON.stringify(body)
  });

export const renamePasskey = (
  accountId: number | string,
  passkeyId: number,
  name: string
) =>
  request<AccountPasskey>(`${accountId}/passkey/${passkeyId}`, {
    method: 'PUT',
    body: JSON.stringify({ name })
  });

/** Your own passkey needs the confirmation; a system user removing someone else's sends none. */
export const deletePasskey = (
  accountId: number | string,
  passkeyId: number,
  confirmation?: PasskeyConfirmation
) =>
  request<null>(`${accountId}/passkey/${passkeyId}`, {
    method: 'DELETE',
    ...(confirmation ? { body: JSON.stringify(confirmation) } : {})
  });

/** System users only, on someone else's account. */
export const deleteAllPasskeys = (accountId: number | string) =>
  request<null>(`${accountId}/passkey`, { method: 'DELETE' });
